<template>
  <div class="settings-content">
    <div
      class="settings-menu settings-menu-title options"
      @click="changePasswordDialog = true"
    >
      {{ $t("ChangePassword") }}
    </div>

    <div
      class="settings-menu settings-menu-title options disabled"
      @click="changeTwoFactorAuthentication"
    >
      {{ twoFactorAuthentication ? $t("Deactivate2FA") : $t("Activate2FA") }}
    </div>

    <div 
      class="settings-menu settings-menu-title options" 
      style="display: flex; align-items: center; gap: 8px"
    >
      <span>{{ $t("Metric") }}</span>
        <v-switch 
          v-model="isImperial"
          @change="confirmPageRefreshDialog=true"
        ></v-switch>
      <span>{{ $t("Imperial") }}</span>
    </div>
    
    <div class="settings-menu select-language">
      <div class="settings-menu-title">{{ $t("Language") }}</div>
      <treeselect
        v-model="selectedLanguage"
        :options="options"
        class="settings-menu-select"
        :clearable="false"
        disableFuzzyMatching
      />
    </div>

    <change-password-dialog
      :open="changePasswordDialog"
      @close="changePasswordDialog = false"
    />
    <activate-two-authentication-dialog
      :open="openTwoFactorAuthenticationDialog"
      @close="openTwoFactorAuthenticationDialog = false"
      @activateAuthenticate="twoFactorAuthentication = true"
    />
    <app-dialog-box
      :open="confirmPageRefreshDialog"
      :title="$t('RefreshPage')"
      :content="$t('ConfirmRefreshPage')"
      :main-action-label="$t('No')"
      @mainAction="cancelMeasuringSystemchange"
      :secondary-action-label="$t('Yes')"
      @secondaryAction="changeMeasureSystem"
    />
  </div>
</template>

<script>
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";
import ChangePasswordDialog from "./ChangePasswordDialog";
import AppDialogBox from "@/components/_Molecules/AppDialogBox";
import ActivateTwoAuthenticationDialog from "@/components/User/Navbar/UserWindow/ActivateTwoAuthenticationDialog";
import { mapActions, mapMutations } from "vuex";
export default {
  name: "UserSettingsMenu",
  components: { ActivateTwoAuthenticationDialog, ChangePasswordDialog, AppDialogBox },
  mixins: [checkPermissionsMixin],

  props: {
    /**
     * Determines if, when starting the dialogbox, activate 2fa is open
     */
    twoFactorAuthenticationPopUp: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      /**
       * Determines the selected language
       */
      selectedLanguage: "en_uk",

      /**
       * Is a metric system
       */
      isImperial: localStorage.getItem("measureSystem") === 'imperial',

      /**
       * Determines available language options
       */
      options: [
        { label: "English (United States)", id: "en_us" },
        { label: "English (United Kingdom)", id: "en_uk" },
        { label: "Português", id: "pt" },
        { label: "Français", id: "fr" },
        { label: "Español", id: "es" },
        { label: "Deutsch", id: "de" }
      ],

      /**
       * Determines if change password dialog is open
       */
      changePasswordDialog: false,

      /**
       * Determines if the 2fa enable dialog box is open
       */
      openTwoFactorAuthenticationDialog: false,
      /**
       * Determines if the user has 2fa active
       */
      twoFactorAuthentication: false,
      /**
       * Determines if the dialog box is open when changing the measuring system
       */
      confirmPageRefreshDialog: false
    };
  },

  watch: {
    /**
     * Whenever the selectedLanguage changes, the setLanguageMessages method is called.
     */
    selectedLanguage() {
      this.setLanguageMessages();
    },

  },

  mounted() {
    this.getLanguageFromLocalStorage();
    this.setLanguageMessages();
    if (this.$store.getters.hasTwoAuthentication !== "null") {
      this.twoFactorAuthentication = this.$store.getters.hasTwoAuthentication;
    }
    this.openTwoFactorAuthenticationPopUp();
  },

  methods: {
    ...mapMutations(["setTwoAuthentication"]),
    ...mapActions(["removeQRCode", "changeUserPreference"]),

    async changeMeasureSystem(){
      let measureSystem;
      if (this.isImperial) {
        measureSystem = "imperial";
        localStorage.setItem("measureSystem","imperial");
      } else {
        measureSystem = "metric"
        localStorage.setItem("measureSystem","metric");
      }
      const userID = parseInt(this.$store.getters.currentUserID);
      const data = { user_id:  userID, measurement_system: measureSystem };
      try {
        await this.changeUserPreference(data);
        this.$router.go(this.$router.currentRoute);
      } catch(e) {
        this.$toasted.global.toast_error(
          this.$i18n.t("ErrorInOperation")
        );
      } finally {
        this.confirmPageRefreshDialog = false;
      }
    },

    cancelMeasuringSystemchange(){
      this.isImperial = !this.isImperial;
      this.confirmPageRefreshDialog = false;
    },

    /** Applies the saved language **/
    getLanguageFromLocalStorage() {
      let previousLanguageID = localStorage.getItem("lang");
      if (previousLanguageID !== null) {
        this.selectedLanguage = previousLanguageID;
      }
    },

    /**
     * Sets messages for the selected language
     */
    setLanguageMessages() {
      this.$i18n.locale = this.selectedLanguage;
      this.$i18n.setLocaleMessage(
        this.selectedLanguage,
        require(`@/langs/${this.selectedLanguage}.json`)
      );
      this.setLanguageInLocalStorage();
      const data = {user_id: parseInt(this.$store.getters.currentUserID), language: this.selectedLanguage};
      this.changeUserPreference(data);
    },

    /**
     * Sets selected language in localStorage
     */
    setLanguageInLocalStorage() {
      localStorage.setItem("lang", this.selectedLanguage);
    },

    /**
     * Opens the dialog box to activate 2fa or deactivate it according to
     * the current value of the twoFactorAuthentication variable
     */
    changeTwoFactorAuthentication() {
      this.twoFactorAuthentication
        ? this.removeTwoFactorAuthentication()
        : (this.openTwoFactorAuthenticationDialog = true);
    },

    /**
     * Request BE for 2fa remover
     * @returns {Promise<void>}
     */
    async removeTwoFactorAuthentication() {
      try {
        await this.removeQRCode();
        this.setTwoAuthentication("null");
        this.$toasted.global.toast_success(
          this.$i18n.t("DeactivateAuthenticateSuccessfully")
        );

        this.twoFactorAuthentication = false;

        if (localStorage.getItem('2fa_required') === 'true' && localStorage.getItem('user_has_2fa') === 'null') {
          this.openTwoFactorAuthenticationDialog = true;
        }
      } catch (e) {
        console.log(e);
      }
    },

    /**
     * Open the dialog box to activate 2fa if it is mandatory to have 2fa active, if the twoFactorAuthenticationPopUp
     * variable is true and if the user does not have 2fa
     */
    openTwoFactorAuthenticationPopUp() {
      if (
        localStorage.getItem("2fa_required") === "true" &&
        this.twoFactorAuthenticationPopUp &&
        localStorage.getItem("user_has_2fa") === "null"
      ) {
        this.openTwoFactorAuthenticationDialog = true;
      }
    }
  }
};
</script>

<style scoped>
.settings-content {
  text-align: left;
  padding: 5px;
  font-size: 10px;
  width: 150px;
  margin: 0 15px;
}

.settings-menu {
  padding: 10px 0;
}

.settings-menu-title {
  font-size: 11px;
  font-weight: 700;
  margin-bottom: 5px;
  text-transform: capitalize;
  padding-left: 2px;
  display: inline-block;
}

.select-language .settings-menu-select {
  height: 30px;
  font-size: 10px;
  color: #231E46;
  margin: 12px 2px 2px -1px;
  background-color: transparent;
  border-radius: 2px;
  max-height: 200px;
  text-transform: uppercase;
}

.select-language .settings-menu-select > div {
  border: none;
  padding: 0;
}

.select-language .v-select .open-indicator:before {
  color: #231E46;
  height: 6px;
  width: 6px;
  transform: rotate(135deg);
}

.select-language .v-select .selected-tag {
  color: #231E46;
}

.select-language .v-select.single.open .selected-tag {
  opacity: 0.5;
}

.select-language .v-select .dropdown-menu {
  background-color: white;
  color: #231E46;
  border-radius: 4px;
  overflow: auto;
  max-height: 200px;
  border: none;
}

.select-language .v-select .dropdown-menu .active > a {
  color: #231E46;
  background: #ffffff;
}

.select-language .v-select .dropdown-menu > .highlight > a {
  background: #dcdcdd;
}

.select-language .dropdown-menu li a {
  color: #231E46;
  margin-left: -5px;
}

.select-language .v-select .vs__actions {
  position: relative;
  top: -1px;
  padding: 0;
  height: unset;
}

.select-language .v-select input[type="search"] {
  top: 0;
}

.options {
  cursor: pointer;
}
.options:hover {
  color: #231E46;
}
.options.selected {
  font-weight: bold;
  color: #231E46;
}

.settings-menu-select >>> .vue-treeselect__control {
  border-radius: 8px !important;
}
.disabled {
  pointer-events: none;
  opacity: 0.5;
}
</style>



// WEBPACK FOOTER //
// src/components/User/Navbar/UserWindow/SettingsMenu.vue